import apiClient from '../api.js';

/**
 * ADATA Review Analytics API Service
 * 提供 ADATA 產品評論分析相關的 API 調用
 */
class AdataReviewService {
  
  /**
   * 取得 ADATA 評論統計摘要
   */
  async getReviewSummary() {
    try {
      const response = await apiClient.get('/adata-reviews/analytics/summary');
      console.log('✅ ADATA Review Summary loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch ADATA review summary:', error);
      this._handleApiError('getReviewSummary', error);
      throw error;
    }
  }

  /**
   * 取得 ADATA 評論來源分析
   */
  async getReviewSources() {
    try {
      const response = await apiClient.get('/adata-reviews/analytics/sources');
      console.log('✅ ADATA Review Sources loaded:', response.data?.length, 'sources');
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch ADATA review sources:', error);
      this._handleApiError('getReviewSources', error);
      throw error;
    }
  }

  /**
   * 取得 ADATA 評分分佈
   */
  async getRatingDistribution() {
    try {
      const response = await apiClient.get('/adata-reviews/analytics/rating-distribution');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch ADATA rating distribution:', error);
      throw error;
    }
  }

  /**
   * 取得品牌情感分析
   */
  async getBrandSentiment() {
    try {
      const response = await apiClient.get('/adata-reviews/analytics/brand-sentiment');
      return response.data.brand_sentiment;
    } catch (error) {
      console.error('Failed to fetch brand sentiment:', error);
      throw error;
    }
  }

  /**
   * 取得評論洞察
   */
  async getReviewInsights() {
    try {
      const response = await apiClient.get('/adata-reviews/analytics/insights');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch review insights:', error);
      throw error;
    }
  }

  /**
   * 取得熱門產品排行
   */
  async getTopProducts(limit = 10) {
    try {
      const response = await apiClient.get(`/adata-reviews/top-products?limit=${limit}`);
      return response.data.products;
    } catch (error) {
      console.error('Failed to fetch top products:', error);
      throw error;
    }
  }

  /**
   * 取得最新評論
   */
  async getRecentReviews(limit = 20, productId = null) {
    try {
      let url = `/adata-reviews/recent?limit=${limit}`;
      if (productId) {
        url += `&product_id=${productId}`;
      }
      const response = await apiClient.get(url);
      return response.data.data; // 分頁回應中的 data 欄位
    } catch (error) {
      console.error('Failed to fetch recent reviews:', error);
      throw error;
    }
  }

  /**
   * 取得完整的評論分析資料
   */
  async getCompleteAnalytics() {
    try {
      const response = await apiClient.get('/adata-reviews/analytics');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch complete analytics:', error);
      throw error;
    }
  }

  /**
   * 批量載入所有資料 (用於頁面初始化)
   */
  async loadAllData() {
    try {
      console.log('🚀 開始載入所有 ADATA Review 資料...');
      
      const [
        summary,
        sources,
        ratingDistribution,
        brandSentiment,
        insights,
        topProducts,
        recentReviews
      ] = await Promise.all([
        this.getReviewSummary(),
        this.getReviewSources(),
        this.getRatingDistribution(),
        this.getBrandSentiment(),
        this.getReviewInsights(),
        this.getTopProducts(5),
        this.getRecentReviews(6)
      ]);

      console.log('🎉 所有 ADATA Review 資料載入完成');
      
      return {
        summary,
        sources,
        ratingDistribution,
        brandSentiment,
        insights,
        topProducts,
        recentReviews
      };
    } catch (error) {
      console.error('❌ Failed to load all ADATA review data:', error);
      throw error;
    }
  }

  /**
   * 處理 API 錯誤
   * @private
   */
  _handleApiError(methodName, error) {
    const errorInfo = {
      method: methodName,
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.response?.data?.detail || error.message,
      timestamp: new Date().toISOString()
    };
    
    console.error(`API Error [${methodName}]:`, errorInfo);
    
    // 可以在這裡添加錯誤追蹤或通知邏輯
    if (error.response?.status === 401) {
      console.warn('🔐 Authentication required - redirecting to login');
      // 可以觸發重新登入邏輯
    } else if (error.response?.status >= 500) {
      console.error('🚨 Server error detected');
      // 可以觸發錯誤通知
    }
  }
}

// 建立單例實例
const adataReviewService = new AdataReviewService();

export default adataReviewService;